import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Plus, Trash, MagnifyingGlass, X, PencilSimple, Check, User, CaretRight, CaretDown } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";
import { useAuth } from "../../contexts/AuthContext";

const API = `${window.location.origin}/api`;

/**
 * Rejony kurierskie — courier zones.
 * Admin: creates/renames/reassigns/deletes rejon, manages strefy + ulice, global search.
 * Employee: sees only the rejon assigned to them, manages strefy + ulice, search.
 */
export default function RejonyTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rejony, setRejony] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRejonId, setSelectedRejonId] = useState(null);
  const [expandedStrefy, setExpandedStrefy] = useState(new Set());
  const toggleStrefa = (id) => setExpandedStrefy((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Global street search
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // Admin: create-new-rejon dialog
  const [creating, setCreating] = useState(false);
  const [newRejonName, setNewRejonName] = useState("");
  const [newRejonEmployeeId, setNewRejonEmployeeId] = useState("");

  // Rename rejon (admin)
  const [renaming, setRenaming] = useState(null); // { id, name, employee_id }

  // Add strefa/ulica inputs
  const [newStrefaName, setNewStrefaName] = useState("");
  const [newUlicaByStrefa, setNewUlicaByStrefa] = useState({}); // { strefaId: "" }

  const loadRejony = async () => {
    try {
      const { data } = await axios.get(`${API}/rejony`);
      setRejony(data || []);
      if (data && data.length && !selectedRejonId) {
        setSelectedRejonId(data[0].id);
      }
    } catch (e) {
      console.error("load rejony failed", e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rej = axios.get(`${API}/rejony`);
        const emp = isAdmin ? axios.get(`${API}/employees`) : Promise.resolve({ data: [] });
        const [rejRes, empRes] = await Promise.all([rej, emp]);
        if (cancelled) return;
        const list = rejRes.data || [];
        setRejony(list);
        if (list.length) setSelectedRejonId(list[0].id);
        setEmployees((empRes.data || []).filter((e) => e.role === "employee" || !e.role));
      } catch (e) {
        console.error("load initial rejony failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Search debounce
  useEffect(() => {
    const q = searchQ.trim();
    if (!q) { setSearchResults([]); setShowSearch(false); return; }
    setShowSearch(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/rejony/search`, { params: { q } });
        setSearchResults(data || []);
      } catch (e) {
        console.error("search failed", e);
        setSearchResults([]);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [searchQ]);

  const selectedRejon = useMemo(
    () => rejony.find((r) => r.id === selectedRejonId) || null,
    [rejony, selectedRejonId]
  );

  const empName = (id) => {
    const e = employees.find((x) => String(x._id) === String(id));
    return e ? (e.name || e.imie_nazwisko || id) : "";
  };

  // ============== ADMIN ACTIONS ==============
  const handleCreateRejon = async () => {
    if (!newRejonName.trim() || !newRejonEmployeeId) return;
    try {
      const { data } = await axios.post(`${API}/rejony`, {
        name: newRejonName.trim(),
        employee_id: newRejonEmployeeId,
      });
      setRejony((prev) => [...prev, data]);
      setSelectedRejonId(data.id);
      setCreating(false);
      setNewRejonName("");
      setNewRejonEmployeeId("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się utworzyć rejonu");
    }
  };

  const handleRenameSave = async () => {
    if (!renaming) return;
    try {
      const body = {};
      if (renaming.name?.trim()) body.name = renaming.name.trim();
      if (renaming.employee_id) body.employee_id = renaming.employee_id;
      await axios.put(`${API}/rejony/${renaming.id}`, body);
      setRenaming(null);
      await loadRejony();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się zapisać");
    }
  };

  const handleDeleteRejon = async (rejonId) => {
    if (!window.confirm("Usunąć cały rejon wraz ze strefami i ulicami?")) return;
    try {
      await axios.delete(`${API}/rejony/${rejonId}`);
      setRejony((prev) => prev.filter((r) => r.id !== rejonId));
      if (selectedRejonId === rejonId) setSelectedRejonId(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  // ============== SHARED ACTIONS (admin + assigned employee) ==============
  const handleAddStrefa = async () => {
    if (!selectedRejon || !newStrefaName.trim()) return;
    try {
      const { data } = await axios.post(
        `${API}/rejony/${selectedRejon.id}/strefy`,
        { name: newStrefaName.trim() }
      );
      setRejony((prev) => prev.map((r) => r.id === selectedRejon.id
        ? { ...r, strefy: [...(r.strefy || []), { ...data, ulice: [] }] }
        : r));
      setNewStrefaName("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się dodać strefy");
    }
  };

  const handleDeleteStrefa = async (strefaId) => {
    if (!selectedRejon) return;
    if (!window.confirm("Usunąć strefę wraz z ulicami?")) return;
    try {
      await axios.delete(`${API}/rejony/${selectedRejon.id}/strefy/${strefaId}`);
      setRejony((prev) => prev.map((r) => r.id === selectedRejon.id
        ? { ...r, strefy: (r.strefy || []).filter((s) => s.id !== strefaId) }
        : r));
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  const handleAddUlica = async (strefaId) => {
    const val = (newUlicaByStrefa[strefaId] || "").trim();
    if (!selectedRejon || !val) return;
    try {
      const { data } = await axios.post(
        `${API}/rejony/${selectedRejon.id}/strefy/${strefaId}/ulice`,
        { name: val }
      );
      setRejony((prev) => prev.map((r) => r.id === selectedRejon.id
        ? {
            ...r,
            strefy: (r.strefy || []).map((s) => s.id === strefaId
              ? { ...s, ulice: [...(s.ulice || []), data] }
              : s),
          }
        : r));
      setNewUlicaByStrefa((p) => ({ ...p, [strefaId]: "" }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się dodać ulicy");
    }
  };

  const handleDeleteUlica = async (strefaId, ulicaId) => {
    if (!selectedRejon) return;
    try {
      await axios.delete(
        `${API}/rejony/${selectedRejon.id}/strefy/${strefaId}/ulice/${ulicaId}`
      );
      setRejony((prev) => prev.map((r) => r.id === selectedRejon.id
        ? {
            ...r,
            strefy: (r.strefy || []).map((s) => s.id === strefaId
              ? { ...s, ulice: (s.ulice || []).filter((u) => u.id !== ulicaId) }
              : s),
          }
        : r));
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  const jumpToResult = (r) => {
    setSelectedRejonId(r.rejon_id);
    setShowSearch(false);
    setSearchQ("");
    // Auto-expand the target strefa so its ulice are visible
    setExpandedStrefy((prev) => new Set([...prev, r.strefa_id]));
    // Scroll to strefa
    setTimeout(() => {
      const el = document.getElementById(`strefa-${r.strefa_id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-500">Ładowanie rejonów...</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6" data-testid="rejony-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <MapPin className="w-6 h-6 text-blue-700" weight="fill" />
            Rejony kurierskie
          </h2>
          <p className="text-xs sm:text-base text-slate-600 mt-1">
            {isAdmin
              ? "Twórz rejony, przypisuj je do kurierów i zarządzaj strefami z ulicami."
              : "Twój rejon dostawczy. Dodawaj strefy i ulice, wyszukuj przypisania."}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium shadow-sm"
            data-testid="rejony-new-btn"
          >
            <Plus className="w-4 h-4" weight="bold" /> Nowy rejon
          </button>
        )}
      </div>

      {/* Global search */}
      <div className="relative">
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Szukaj ulicy (np. Lipowa)..."
            className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
            data-testid="rejony-street-search"
          />
        </div>
        {showSearch && searchQ.trim() && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
            {searchResults.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500">Brak wyników.</div>
            ) : (
              searchResults.map((r) => (
                <button
                  key={r.ulica_id}
                  onClick={() => jumpToResult(r)}
                  className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-0 text-sm flex items-center justify-between"
                  data-testid={`rejony-search-result-${r.ulica_id}`}
                >
                  <span className="font-medium text-slate-800">{r.ulica}</span>
                  <span className="text-xs text-slate-500">
                    {r.strefa} · <span className="text-blue-700">{r.rejon}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {rejony.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-slate-500 shadow-sm border border-slate-200">
          {isAdmin
            ? "Brak rejonów. Utwórz pierwszy klikając „Nowy rejon”."
            : "Nie masz przypisanego rejonu. Skontaktuj się z administratorem."}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          {/* Rejony list */}
          <aside className="bg-white rounded-xl border border-slate-200 shadow-sm p-2 h-max">
            <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1.5">
              Rejony ({rejony.length})
            </div>
            <ul className="space-y-1">
              {rejony.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelectedRejonId(r.id)}
                    data-testid={`rejon-item-${r.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedRejonId === r.id
                        ? "bg-blue-700 text-white"
                        : "hover:bg-slate-100 text-slate-700"
                    }`}
                  >
                    <div className="font-medium truncate">{r.name}</div>
                    <div className={`text-xs mt-0.5 flex items-center gap-1 ${
                      selectedRejonId === r.id ? "text-blue-100" : "text-slate-500"
                    }`}>
                      <User className="w-3 h-3" />
                      {r.employee_name || empName(r.employee_id) || "—"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Selected rejon details */}
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
            {!selectedRejon ? (
              <div className="text-center py-8 text-slate-500">Wybierz rejon z listy.</div>
            ) : (
              <>
                {/* Rejon header */}
                <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-slate-200">
                  <div className="flex-1 min-w-0">
                    {renaming?.id === selectedRejon.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={renaming.name}
                          onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                          className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                          placeholder="Nazwa rejonu"
                          data-testid="rejon-rename-input"
                        />
                        <select
                          value={renaming.employee_id}
                          onChange={(e) => setRenaming({ ...renaming, employee_id: e.target.value })}
                          className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                          data-testid="rejon-reassign-select"
                        >
                          {employees.map((e) => (
                            <option key={e._id} value={e._id}>
                              {e.name || e.imie_nazwisko}
                            </option>
                          ))}
                        </select>
                        <div className="flex gap-2">
                          <button
                            onClick={handleRenameSave}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-xs font-medium"
                            data-testid="rejon-rename-save"
                          >
                            <Check className="w-3.5 h-3.5" /> Zapisz
                          </button>
                          <button
                            onClick={() => setRenaming(null)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs"
                          >
                            <X className="w-3.5 h-3.5" /> Anuluj
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-lg sm:text-xl font-bold text-slate-800 truncate">
                          {selectedRejon.name}
                        </h3>
                        <div className="text-xs sm:text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5" />
                          Kurier: <span className="font-medium text-slate-700">
                            {selectedRejon.employee_name || empName(selectedRejon.employee_id) || "—"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  {isAdmin && renaming?.id !== selectedRejon.id && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => setRenaming({
                          id: selectedRejon.id,
                          name: selectedRejon.name,
                          employee_id: selectedRejon.employee_id,
                        })}
                        className="p-2 text-slate-500 hover:bg-slate-100 rounded-md"
                        title="Edytuj"
                        data-testid="rejon-edit-btn"
                      >
                        <PencilSimple className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRejon(selectedRejon.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Usuń rejon"
                        data-testid="rejon-delete-btn"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Add strefa */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  <input
                    type="text"
                    value={newStrefaName}
                    onChange={(e) => setNewStrefaName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddStrefa()}
                    placeholder="Nazwa nowej strefy (np. Strefa 1)"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid="rejon-new-strefa-input"
                  />
                  <button
                    onClick={handleAddStrefa}
                    disabled={!newStrefaName.trim()}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="rejon-add-strefa-btn"
                  >
                    <Plus className="w-4 h-4" weight="bold" /> Dodaj strefę
                  </button>
                </div>

                {/* Strefy list */}
                {(!selectedRejon.strefy || selectedRejon.strefy.length === 0) ? (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    Brak stref. Dodaj pierwszą powyżej.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedRejon.strefy.map((strefa) => (
                      <div
                        key={strefa.id}
                        id={`strefa-${strefa.id}`}
                        className="border border-slate-200 rounded-lg overflow-hidden"
                        data-testid={`strefa-${strefa.id}`}
                      >
                        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                          <button
                            onClick={() => toggleStrefa(strefa.id)}
                            className="flex items-center gap-1.5 font-semibold text-slate-800 text-sm flex-1 text-left hover:text-blue-700"
                            data-testid={`strefa-toggle-${strefa.id}`}
                          >
                            {expandedStrefy.has(strefa.id) ? <CaretDown className="w-3.5 h-3.5" weight="bold" /> : <CaretRight className="w-3.5 h-3.5" weight="bold" />}
                            {strefa.name}
                            <span className="ml-2 text-xs text-slate-500 font-normal">
                              ({(strefa.ulice || []).length} {(strefa.ulice || []).length === 1 ? "ulica" : "ulic"})
                            </span>
                          </button>
                          <button
                            onClick={() => handleDeleteStrefa(strefa.id)}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-md"
                            title="Usuń strefę"
                            data-testid={`strefa-delete-${strefa.id}`}
                          >
                            <Trash className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {expandedStrefy.has(strefa.id) && (
                        <div className="p-3">
                          {/* Add ulica */}
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={newUlicaByStrefa[strefa.id] || ""}
                              onChange={(e) => setNewUlicaByStrefa({
                                ...newUlicaByStrefa,
                                [strefa.id]: e.target.value,
                              })}
                              onKeyDown={(e) => e.key === "Enter" && handleAddUlica(strefa.id)}
                              placeholder="Dodaj ulicę..."
                              className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                              data-testid={`ulica-input-${strefa.id}`}
                            />
                            <button
                              onClick={() => handleAddUlica(strefa.id)}
                              disabled={!(newUlicaByStrefa[strefa.id] || "").trim()}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`ulica-add-${strefa.id}`}
                            >
                              <Plus className="w-3.5 h-3.5" /> Dodaj
                            </button>
                          </div>

                          {/* Ulice pills */}
                          {(!strefa.ulice || strefa.ulice.length === 0) ? (
                            <div className="text-xs text-slate-400 italic px-1 py-2">
                              Brak ulic w tej strefie.
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {strefa.ulice.map((u) => (
                                <span
                                  key={u.id}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-800 rounded-md text-xs font-medium border border-blue-100"
                                  data-testid={`ulica-${u.id}`}
                                >
                                  {u.name}
                                  <button
                                    onClick={() => handleDeleteUlica(strefa.id, u.id)}
                                    className="text-blue-600 hover:text-red-600"
                                    title="Usuń ulicę"
                                    data-testid={`ulica-delete-${u.id}`}
                                  >
                                    <X className="w-3 h-3" weight="bold" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {/* Create rejon modal (admin) */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="rejony-create-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Nowy rejon</h3>
              <button onClick={() => setCreating(false)} className="p-1 hover:bg-slate-100 rounded-md">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Nazwa rejonu</label>
                <input
                  type="text"
                  value={newRejonName}
                  onChange={(e) => setNewRejonName(e.target.value)}
                  placeholder="np. Warszawa Centrum"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="rejony-create-name"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Przypisz do kuriera</label>
                <select
                  value={newRejonEmployeeId}
                  onChange={(e) => setNewRejonEmployeeId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="rejony-create-employee"
                >
                  <option value="">— wybierz kuriera —</option>
                  {employees.map((e) => (
                    <option key={e._id} value={e._id}>
                      {e.name || e.imie_nazwisko}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setCreating(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleCreateRejon}
                  disabled={!newRejonName.trim() || !newRejonEmployeeId}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="rejony-create-submit"
                >
                  Utwórz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
